import { DateTime } from "luxon";
import { useMemo } from "react";
import { generateAuditEntryTitle } from "@/domains/audit/utils/audit-utils";
import type { AuditEntry } from "@/gql/graphql";
import { getUserFriendlyErrorMessage } from "@/lib/error/error-utils";
import { timezoneUtils } from "@/lib/timezone";
import { useAuditQuery } from "./use-audit-query";

export interface UseAuditOptions {
  salonId?: string | null;
  limit?: number;
  tableNames?: string[];
  userId?: string | null;
  entityId?: string | null;
}

export interface ProcessedAuditEntry extends AuditEntry {
  formattedTimestamp: string;
  relativeTime: string;
  operationColor: string;
  operationIcon: string;
  title: string;
}

export interface UseAuditReturn {
  auditEntries: ProcessedAuditEntry[];
  totalCount: number;
  loading: boolean;
  error: any;
  errorMessage: string | null;
  retry: () => void;
  refetch: () => Promise<any>;
}

/**
 * Main hook for audit logs with data transformation and user-friendly error handling
 */
export const useAudit = ({
  salonId,
  limit = 20,
  tableNames,
  userId,
  entityId,
}: UseAuditOptions = {}): UseAuditReturn => {
  const { data, totalCount, loading, error, retry, refetch } = useAuditQuery({
    salonId,
    limit,
    tableNames,
    userId,
    entityId,
  });

  // Transform audit entries with additional display information
  const auditEntries = useMemo(() => {
    if (!data) return [];

    const currentTime = DateTime.now();

    return data.map((entry): ProcessedAuditEntry => {
      const entryTime = entry.timestamp ? timezoneUtils.fromISO(entry.timestamp) : DateTime.now();

      // Calculate relative time
      const relativeTime = entryTime.toRelative({ base: currentTime }) || "just now";

      // Format timestamp for display in salon timezone
      const formattedTimestamp = entryTime.toFormat("MMM d, yyyy h:mm a (ZZZZ)");

      // Get operation styling
      const { color, icon } = getOperationStyle(entry.operation);

      // Generate title
      const title = generateAuditEntryTitle(entry);

      return {
        ...entry,
        formattedTimestamp,
        relativeTime,
        operationColor: color,
        operationIcon: icon,
        title,
      };
    });
  }, [data]);

  // Get user-friendly error message
  const errorMessage = useMemo(() => {
    if (!error) return null;
    return getUserFriendlyErrorMessage(error);
  }, [error]);

  return {
    auditEntries,
    totalCount,
    loading,
    error,
    errorMessage,
    retry,
    refetch,
  };
};

/**
 * Get operation styling based on operation type
 */
function getOperationStyle(operation: string): { color: string; icon: string } {
  switch (operation) {
    case "CREATE":
      return {
        color: "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200",
        icon: "plus",
      };
    case "UPDATE":
      return {
        color: "bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200",
        icon: "edit",
      };
    case "DELETE":
      return {
        color: "bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200",
        icon: "trash",
      };
    default:
      return {
        color: "bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200",
        icon: "info",
      };
  }
}
